import React, {useCallback, useMemo} from 'react';
import {FocusScope} from '@react-aria/focus';
import {mergeProps} from '@react-aria/utils';
import {
    DismissButton,
    OverlayContainer,
    useOverlay,
    useOverlayPosition,
    useOverlayTrigger,
} from '@react-aria/overlays';
import {type OverlayTriggerState, useOverlayTriggerState} from '@react-stately/overlays';
import type {AriaPositionProps} from '@react-aria/overlays';
import type {OverlayTriggerProps} from '@react-types/overlays';
import type {FocusableElement} from '@react-types/shared';
import type {AriaButtonProps} from '@react-aria/button';

export const Popover = React.forwardRef<Element, any>(
    ({children, isOpen, onClose, style, shouldCloseOnInteractOutside, ...otherProps}, passedRef) => {
        const innerRef = React.createRef<Element>();
        const ref = (passedRef || innerRef) as React.RefObject<FocusableElement>;

        const {overlayProps, underlayProps} = useOverlay(
            {
                onClose,
                isOpen,
                isDismissable: true,
                shouldCloseOnBlur: true,
                shouldCloseOnInteractOutside,
            },
            ref,
        );

        return (
            <FocusScope restoreFocus={true}>
                <div
                    {...mergeProps(overlayProps, otherProps)}
                    ref={passedRef}
                    style={{...style, visibility: isOpen ? 'visible' : 'hidden'}}
                >
                    {React.cloneElement(children, underlayProps)}
                    <DismissButton onDismiss={onClose} />
                </div>
            </FocusScope>
        );
    },
);

type Props = {
    triggerNode?: Element | null;
    state?: OverlayTriggerState;
    zIndex?: number;
    renderTriggerElement?: (
        triggerProps: AriaButtonProps,
        ref: React.RefObject<HTMLButtonElement>,
        state: OverlayTriggerState
    ) => React.ReactNode;
    shouldCloseOnInteractOutside?: (element: Element) => boolean;
} & OverlayTriggerProps &
Omit<Partial<AriaPositionProps>, 'targetRef' | 'overlayRef' | 'isOpen'>;

export function PopoverOverlay(props: React.PropsWithChildren<Props>) {
    const {
        triggerNode,
        maxHeight,
        placement,
        containerPadding = 0,
        offset = 5,
        crossOffset,
        shouldFlip = true,
        renderTriggerElement,
        state: outsideState,
        shouldCloseOnInteractOutside,
        ...overlayTriggerProps
    } = props;
    const innerState = useOverlayTriggerState(overlayTriggerProps);
    const state = outsideState || innerState;
    const overlayRef = React.useRef<HTMLDivElement>(null);

    const triggerRef = React.useRef<HTMLButtonElement>(null);

    const ref = useMemo(() => {
        return triggerNode ? {
            current: triggerNode,
        } : triggerRef;
    }, [triggerNode]);

    const defaultShouldCloseOnInteractOutside = useCallback((element?: HTMLElement | null) => {
        // element передается из обработчика onPointerDown и в некоторых случаях может быть undefined
        // возможно , баг был исправлен в https://github.com/adobe/react-spectrum/issues/4121
        if (!element) {
            return false;
        }
        return !ref.current?.contains(element);
    }, [ref]);

    const {overlayProps, triggerProps} = useOverlayTrigger({type: 'menu'}, state, ref);

    const {overlayProps: positionProps} = useOverlayPosition({
        targetRef: ref,
        overlayRef,
        placement,
        containerPadding,
        offset,
        isOpen: state.isOpen,
        crossOffset,
        shouldFlip,
        maxHeight,
    });

    return (
        <>
            {renderTriggerElement?.(triggerProps, triggerRef, state)}
            {state.isOpen ? (
                <OverlayContainer>
                    <Popover
                        {...overlayProps}
                        {...positionProps}
                        ref={overlayRef}
                        isOpen={state.isOpen}
                        style={{...positionProps.style, zIndex: props.zIndex ?? positionProps.style?.zIndex}}
                        onClose={state.close}
                        shouldCloseOnInteractOutside={shouldCloseOnInteractOutside ?? defaultShouldCloseOnInteractOutside}
                    >
                        {props.children}
                    </Popover>
                </OverlayContainer>
            ) : null}
        </>
    );
}

